import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { useConfirm } from '../context/ConfirmContext';
import { formatDate } from '../utils/locale';
import { formatRoleLabel } from '../utils/roleLabels';

const PendingInvitationsTable = ({
  companyId,
  invitations,
  onRefresh,
  onCancelSuccess,
  onCancelError,
  allowResend = false,
  onResendSuccess,
  onResendError,
  cancelLabel,
  cancellingLabel,
  resendLabel,
  resendingLabel,
  confirmMessage,
  emptyMessage,
  emptyClassName = ''
}) => {
  const { t, i18n } = useTranslation('common');
  const confirm = useConfirm();
  const [cancellingInvitationId, setCancellingInvitationId] = useState(null);
  const [resendingInvitationId, setResendingInvitationId] = useState(null);

  const handleCancel = async (invitationId) => {
    if (!companyId) {
      return;
    }
    if (!(await confirm(confirmMessage || t('invitationsConfirmCancel'), { danger: true }))) {
      return;
    }

    setCancellingInvitationId(invitationId);

    try {
      const response = await axios.delete(
        `/api/companies/${companyId}/invitations/${invitationId}`
      );
      onCancelSuccess?.(response.data.data?.email);
      try {
        await onRefresh?.();
      } catch (refreshError) {
        console.error('Error refreshing invitations:', refreshError);
      }
    } catch (error) {
      onCancelError?.(formatError(error));
    } finally {
      setCancellingInvitationId(null);
    }
  };

  const handleResend = async (invitationId) => {
    if (!companyId) {
      return;
    }

    setResendingInvitationId(invitationId);

    try {
      const response = await axios.post(
        `/api/companies/${companyId}/invitations/${invitationId}/resend`
      );
      onResendSuccess?.(response.data.data?.email);
      try {
        await onRefresh?.();
      } catch (refreshError) {
        console.error('Error refreshing invitations:', refreshError);
      }
    } catch (error) {
      onResendError?.(formatError(error));
    } finally {
      setResendingInvitationId(null);
    }
  };

  if (!invitations?.length) {
    return <p className={emptyClassName}>{emptyMessage || t('invitationsEmpty')}</p>;
  }

  const isBusy = (invitationId) =>
    cancellingInvitationId === invitationId || resendingInvitationId === invitationId;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>{t('invitationsEmail')}</th>
          <th>{t('invitationsRole')}</th>
          <th>{t('invitationsInvitedBy')}</th>
          <th>{t('invitationsInvitedAt')}</th>
          <th>{t('invitationsExpires')}</th>
          <th>{t('invitationsActions')}</th>
        </tr>
      </thead>
      <tbody>
        {invitations.map((invitation) => (
          <tr key={invitation.id}>
            <td>{invitation.email}</td>
            <td>{formatRoleLabel(invitation.role, t)}</td>
            <td>{invitation.invited_by_name || invitation.invited_by_email || t('invitationsSystem')}</td>
            <td>{formatDate(invitation.created_at, i18n.language) || '—'}</td>
            <td>{formatDate(invitation.expires_at, i18n.language) || '—'}</td>
            <td>
              {allowResend && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!companyId || isBusy(invitation.id)}
                  onClick={() => handleResend(invitation.id)}
                >
                  {resendingInvitationId === invitation.id
                    ? (resendingLabel || t('invitationsResending'))
                    : (resendLabel || t('invitationsResend'))}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!companyId || isBusy(invitation.id)}
                onClick={() => handleCancel(invitation.id)}
              >
                {cancellingInvitationId === invitation.id
                  ? (cancellingLabel || t('invitationsCancelling'))
                  : (cancelLabel || t('cancel'))}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default PendingInvitationsTable;
